import React from 'react';
import { ShoppingBag, X, Minus, Plus, Trash2, ArrowRight } from 'lucide-react';
import { useCart } from '../../hooks/useCart';
import { formatVND } from '../../utils/formatters';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';

export default function CartDrawer({ isOpen, onClose, onOpenCheckout, onOpenAuth }) {
    const {
        cart,
        updateCartQuantity,
        removeFromCart,
        couponCode,
        commitCartQuantity,
        clearCart,
        setCouponCode,
        couponError,
        handleApplyCoupon,
        subtotal,
        discountAmount,
        shippingFee,
        finalTotal,
    } = useCart();

    const { user } = useAuth();
    const { showToast } = useToast();

    if (!isOpen) return null;

    const handleCheckoutClick = () => {
        if (!user) {
            onClose();
            if (onOpenAuth) {
                onOpenAuth();
            }
            showToast('Vui lòng đăng nhập để tiếp tục thanh toán', 'warning');
            return;
        }

        onClose();
        if (onOpenCheckout) {
            onOpenCheckout();
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div
                className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
                <div className="w-screen max-w-md bg-white shadow-2xl flex flex-col">
                    <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <ShoppingBag className="w-5 h-5 text-indigo-600" />
                            <h2 className="text-lg font-bold text-slate-900">Giỏ Hàng Của Bạn</h2>
                        </div>

                        <div className="flex items-center gap-3">
                            {cart.length > 0 && (
                                <button
                                    onClick={() => {
                                        if (window.confirm("Bạn có chắc chắn muốn xóa toàn bộ giỏ hàng không?")) {
                                            clearCart();
                                        }
                                    }}
                                    className="text-xs text-rose-500 hover:text-rose-700 font-semibold flex items-center gap-1 cursor-pointer transition px-2 py-1 rounded-lg hover:bg-rose-50"
                                    title="Xóa tất cả sản phẩm"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    <span>Xóa tất cả</span>
                                </button>
                            )}

                            <button
                                onClick={onClose}
                                className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 cursor-pointer"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Items List */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        {cart.length === 0 ? (
                            <div className="text-center py-12">
                                <ShoppingBag className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                                <p className="text-sm font-semibold text-slate-500">Giỏ hàng đang trống</p>
                            </div>
                        ) : (
                            cart.map((item, index) => {
                                // Đảm bảo lấy đúng đường dẫn ảnh từ Backend (thumbnail)
                                const itemImgUrl = item.thumbnail || item.image || item.images?.[0] || 'https://via.placeholder.com/150';

                                // Đảm bảo số lượng và giá tiền luôn là số hợp lệ
                                const itemQty = Math.max(1, parseInt(item.quantity) || 1);
                                const itemPrice = Number(item.price) || 0;
                                const itemTotalPrice = itemPrice * itemQty;

                                return (
                                    <div
                                        key={index}
                                        className="flex gap-4 p-3 bg-slate-50 rounded-2xl border border-slate-100 relative group"
                                    >
                                        <div className="w-20 h-20 rounded-xl bg-white overflow-hidden shrink-0 border border-slate-200 flex items-center justify-center">
                                            <img
                                                src={itemImgUrl}
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>

                                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                                            <div>
                                                <h4 className="text-xs font-bold text-slate-900 truncate">{item.name}</h4>
                                                <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500">
                                                    {item.selectedSize && <span>Size: {item.selectedSize}</span>}
                                                    {item.selectedColor && (
                                                        <span className="flex items-center gap-1">
                              Màu:{' '}
                                                            <span
                                                                className="w-2.5 h-2.5 rounded-full inline-block border border-slate-300"
                                                                style={{ backgroundColor: item.selectedColor }}
                                                            />
                            </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between mt-2">
                                                <div className="flex items-center border border-slate-200 bg-white rounded-lg overflow-hidden">
                                                    <button
                                                        onClick={() => {
                                                            const newQty = itemQty - 1;
                                                            if (newQty > 0) {
                                                                updateCartQuantity(index, newQty);
                                                                commitCartQuantity(index);
                                                            }
                                                        }}
                                                        className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition cursor-pointer"
                                                    >
                                                        <Minus className="w-3 h-3" />
                                                    </button>

                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={itemQty}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value) || 1;
                                                            // Kiểm tra nếu vượt quá tồn kho (giả định tồn kho lưu ở item.stock hoặc item.quantityInStock)
                                                            const maxStock = item.stock ?? item.quantityInStock;
                                                            if (val > maxStock) {
                                                                showToast(`Chỉ còn lại ${maxStock} sản phẩm trong kho!`, "warning");
                                                                updateCartQuantity(index, maxStock);
                                                            } else {
                                                                updateCartQuantity(index, val);
                                                            }
                                                        }}
                                                        onBlur={() => commitCartQuantity(index)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.target.blur();
                                                            }
                                                        }}
                                                        className="w-10 text-center text-xs font-bold text-slate-800 focus:outline-none bg-transparent [-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                    />

                                                    <button
                                                        onClick={() => {
                                                            const maxStock = item.stock ?? item.quantityInStock;
                                                            const newQty = itemQty + 1;
                                                            if (newQty > maxStock) {
                                                                showToast(`Số lượng đã đạt tối đa trong kho (${maxStock})!`, "warning");
                                                            } else {
                                                                updateCartQuantity(index, newQty);
                                                                commitCartQuantity(index);
                                                            }
                                                        }}
                                                        className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition cursor-pointer"
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                    </button>
                                                </div>

                                                {/* Hiển thị tổng tiền chính xác của sản phẩm này */}
                                                <span className="text-xs font-extrabold text-slate-900">
                                                {formatVND ? formatVND(itemTotalPrice) : `${itemTotalPrice.toLocaleString()} VNĐ`}
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => removeFromCart(index)}
                                            className="text-slate-300 hover:text-rose-500 transition p-1 cursor-pointer"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Checkout Footer */}
                    {cart.length > 0 && (
                        <div className="p-6 border-t border-slate-100 bg-slate-50 space-y-4">
                            <form onSubmit={handleApplyCoupon} className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Mã giảm giá (ví dụ: JapanICHIBA10)"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value)}
                                    className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs uppercase focus:outline-none focus:border-indigo-500"
                                />
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition cursor-pointer"
                                >
                                    Áp Dụng
                                </button>
                            </form>
                            {couponError && <p className="text-[11px] text-rose-500">{couponError}</p>}

                            <div className="space-y-1.5 text-xs">
                                <div className="flex justify-between text-slate-500">
                                    <span>Tạm tính</span>
                                    <span className="font-semibold text-slate-800">
                    {formatVND ? formatVND(subtotal) : `${subtotal?.toLocaleString()} VNĐ`}
                  </span>
                                </div>
                                {discountAmount > 0 && (
                                    <div className="flex justify-between text-emerald-600">
                                        <span>Giảm giá</span>
                                        <span className="font-semibold">
                      -{formatVND ? formatVND(discountAmount) : `${discountAmount?.toLocaleString()} VNĐ`}
                    </span>
                                    </div>
                                )}
                                <div className="flex justify-between text-slate-500">
                                    <span>Phí vận chuyển</span>
                                    <span className="font-semibold text-slate-800">
                    {shippingFee === 0 ? 'Miễn phí' : (formatVND ? formatVND(shippingFee) : `${shippingFee?.toLocaleString()} VNĐ`)}
                  </span>
                                </div>
                                <div className="flex justify-between text-sm font-extrabold text-slate-900 border-t border-slate-200 pt-2">
                                    <span>Tổng tiền</span>
                                    <span className="text-indigo-600">
                    {formatVND ? formatVND(finalTotal) : `${finalTotal?.toLocaleString()} VNĐ`}
                  </span>
                                </div>
                            </div>

                            <button
                                onClick={handleCheckoutClick}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3.5 rounded-xl transition text-sm shadow-lg shadow-indigo-600/20 flex items-center justify-center space-x-2 cursor-pointer"
                            >
                                <span>{user ? 'Tiến Hành Thanh Toán' : 'Đăng Nhập Để Thanh Toán'}</span>
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}